import { useRef, useState } from 'react';
import { motion } from 'motion/react';
import {
  Factory,
  Plus,
  Pencil,
  Trash2,
  Upload,
  ExternalLink,
} from 'lucide-react';
import { useApp } from '@/store';
import { type Manufacturer, type Model } from '@/data/types';
import { useT, PRESSURE_LABEL_KEYS } from '@/lib/i18n';
import { useConfirm } from '@/components/ConfirmDialog';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';
import {
  signAndPut,
  resolveUploadUrl,
  UploadKind,
  UploadFailedError,
} from '@/lib/uploads';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollTable } from '@/components/layout';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type MfrForm = { name: string; documentationPdf: string };
type ModelForm = {
  name: string;
  content: string;
  deviceType: string;
  agentType: string;
  price: string;
  documentationPdf: string;
};
const blankMfr = (): MfrForm => ({ name: '', documentationPdf: '' });
const blankModel = (): ModelForm => ({
  name: '',
  content: '',
  deviceType: '',
  agentType: '',
  price: '',
  documentationPdf: '',
});

export function Manufacturers() {
  const t = useT();
  const confirm = useConfirm();
  const mfrs = useApp(s => s.manufacturers);
  const types = useApp(s => s.deviceTypes);
  const upsertManufacturer = useApp(s => s.upsertManufacturer);
  const deleteManufacturer = useApp(s => s.deleteManufacturer);
  const upsertModel = useApp(s => s.upsertModel);
  const deleteModel = useApp(s => s.deleteModel);
  const toast = useApp(s => s.toast);
  // Hersteller rights (#95) cover the manufacturers and their models.
  const can = useSectionRights()[SectionId.MANUFACTURERS];

  // Catalog is shown alphabetically (client feedback); store order is
  // replication order.
  const sortedMfrs = [...mfrs].sort((a, b) => a.name.localeCompare(b.name));
  const [selected, setSelected] = useState(sortedMfrs[0]?.id ?? '');
  const active = mfrs.find(m => m.id === selected);
  const sortedModels = active
    ? [...active.models].sort((a, b) => a.name.localeCompare(b.name))
    : [];

  const [mfrOpen, setMfrOpen] = useState(false);
  const [editingMfr, setEditingMfr] = useState<Manufacturer | null>(null);
  const [mfrForm, setMfrForm] = useState<MfrForm>(blankMfr());
  const mfrDocRef = useRef<HTMLInputElement>(null);
  const openUpload = async (stored: string) => {
    try {
      window.open(await resolveUploadUrl(stored), '_blank', 'noopener');
    } catch {
      toast(t('common.error'));
    }
  };

  const [modelOpen, setModelOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [modelForm, setModelForm] = useState<ModelForm>(blankModel());
  const modelDocRef = useRef<HTMLInputElement>(null);

  // Manufacturer CRUD
  const openCreateMfr = () => {
    setEditingMfr(null);
    setMfrForm(blankMfr());
    setMfrOpen(true);
  };
  const openEditMfr = (m: Manufacturer) => {
    setEditingMfr(m);
    setMfrForm({ name: m.name, documentationPdf: m.documentationPdf ?? '' });
    setMfrOpen(true);
  };
  const saveMfr = () => {
    if (!mfrForm.name.trim()) return;
    const id = editingMfr?.id ?? `MFR-${Date.now()}`;
    upsertManufacturer({
      id,
      name: mfrForm.name.trim(),
      documentationPdf: mfrForm.documentationPdf || undefined,
      models: editingMfr?.models ?? [],
    });
    if (!editingMfr) setSelected(id);
    toast(editingMfr ? t('common.save') : t('mfr.newMfr'));
    setMfrOpen(false);
  };
  const deleteMfr = async (m: Manufacturer) => {
    if (!(await confirm(`${t('common.delete')} ${m.name}?`))) return;
    deleteManufacturer(m.id);
    setSelected(sortedMfrs.filter(x => x.id !== m.id)[0]?.id ?? '');
    toast(t('common.delete'));
  };

  // Model CRUD
  const openCreateModel = () => {
    setEditingModel(null);
    setModelForm(blankModel());
    setModelOpen(true);
  };
  const openEditModel = (mdl: Model) => {
    setEditingModel(mdl);
    setModelForm({
      name: mdl.name,
      content: mdl.content,
      deviceType: mdl.deviceType,
      agentType: mdl.agentType,
      price: mdl.price != null ? String(mdl.price) : '',
      documentationPdf: mdl.documentationPdf ?? '',
    });
    setModelOpen(true);
  };
  const saveModel = () => {
    if (!active || !modelForm.name.trim() || !modelForm.deviceType) return;
    upsertModel(active.id, {
      id: editingModel?.id ?? `MDL-${Date.now()}`,
      mfrId: active.id,
      name: modelForm.name.trim(),
      content: modelForm.content.trim(),
      deviceType: modelForm.deviceType,
      agentType: modelForm.agentType.trim(),
      // Catalogue price of the device, billed when one is fitted as new (#60).
      price:
        modelForm.price.trim() === '' ? undefined : Number(modelForm.price),
      // Manual for this device; the manufacturer-level one remains the
      // fallback for models left without their own (client #51).
      documentationPdf: modelForm.documentationPdf || undefined,
    });
    toast(editingModel ? t('common.save') : t('mfr.addModel'));
    setModelOpen(false);
  };
  const handleDeleteModel = async (mdl: Model) => {
    if (!active) return;
    if (!(await confirm(`${t('common.delete')} ${mdl.name}?`))) return;
    deleteModel(active.id, mdl.id);
    toast(t('common.delete'));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Factory className="h-6 w-6 text-primary" /> {t('mfr.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('mfr.subtitle')}</p>
        </div>
        {can.create && (
          <Button size="sm" onClick={openCreateMfr} data-focus="mfr-new">
            <Plus className="h-4 w-4" />
            {t('mfr.newMfr')}
          </Button>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{t('mfr.catalog')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {sortedMfrs.map(m => (
              <div key={m.id} className="flex items-center gap-1">
                <button
                  onClick={() => setSelected(m.id)}
                  className={`flex-1 text-left px-3 py-2 rounded-md text-sm transition ${
                    m.id === selected
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'hover:bg-accent'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span>{m.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {m.models.length}
                    </Badge>
                  </div>
                </button>
                {can.edit && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    title={t('common.edit')}
                    aria-label={t('common.edit')}
                    onClick={() => openEditMfr(m)}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                )}
                {can.delete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    title={t('common.delete')}
                    aria-label={t('common.delete')}
                    onClick={() => deleteMfr(m)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">
                {active?.name ?? '—'} {t('mfr.models')}
              </CardTitle>
              {can.create && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openCreateModel}
                  disabled={!active}
                >
                  <Plus className="h-3 w-3" />
                  {t('mfr.addModel')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {active ? (
              <ScrollTable minWidth={500}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('ext.model')}</TableHead>
                      <TableHead>{t('mfr.content')}</TableHead>
                      <TableHead>{t('table.deviceType')}</TableHead>
                      <TableHead>{t('ext.pressure')}</TableHead>
                      <TableHead className="w-20" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedModels.map(mdl => {
                      const dt = types.find(x => x.id === mdl.deviceType);
                      return (
                        <TableRow key={mdl.id}>
                          <TableCell className="font-medium">
                            {mdl.name}
                          </TableCell>
                          <TableCell>{mdl.content}</TableCell>
                          <TableCell className="text-xs">
                            {dt?.nameDe}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {dt
                                ? t(PRESSURE_LABEL_KEYS[dt.pressureClass])
                                : '—'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {can.edit && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-11 w-11 sm:h-7 sm:w-7"
                                  title={t('common.edit')}
                                  aria-label={t('common.edit')}
                                  onClick={() => openEditModel(mdl)}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                              )}
                              {can.delete && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-11 w-11 sm:h-7 sm:w-7"
                                  title={t('common.delete')}
                                  aria-label={t('common.delete')}
                                  onClick={() => handleDeleteModel(mdl)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {active.models.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center text-muted-foreground py-6 text-sm"
                        >
                          {t('common.notFound')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollTable>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('common.notFound')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Manufacturer dialog */}
      <Dialog open={mfrOpen} onOpenChange={setMfrOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingMfr ? t('common.edit') : t('mfr.newMfr')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('mfr.mfrName')}</Label>
              <Input
                value={mfrForm.name}
                onChange={e =>
                  setMfrForm(f => ({ ...f, name: e.target.value }))
                }
              />
            </div>
            <div>
              <Label>{t('ext.uploadDoc')}</Label>
              <input
                ref={mfrDocRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={async e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  // Uploads reject now rather than inlining a data: URL (#48).
                  try {
                    const stored = await signAndPut(file, UploadKind.PDF);
                    setMfrForm(f => ({ ...f, documentationPdf: stored }));
                  } catch (err) {
                    toast(
                      err instanceof UploadFailedError
                        ? t(err.i18nKey)
                        : t('common.error')
                    );
                  }
                  e.target.value = '';
                }}
              />
              <div className="mt-1 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => mfrDocRef.current?.click()}
                >
                  <Upload className="h-3 w-3" />
                  {t('ext.uploadDoc')}
                </Button>
                {mfrForm.documentationPdf && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openUpload(mfrForm.documentationPdf)}
                  >
                    <ExternalLink className="h-3 w-3" />
                    {t('ext.viewDoc')}
                  </Button>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMfrOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={saveMfr}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Model dialog */}
      <Dialog open={modelOpen} onOpenChange={setModelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingModel ? t('common.edit') : t('mfr.addModel')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>{t('ext.model')}</Label>
              <Input
                value={modelForm.name}
                onChange={e =>
                  setModelForm(f => ({ ...f, name: e.target.value }))
                }
              />
            </div>
            <div>
              <Label>{t('mfr.content')}</Label>
              <Input
                value={modelForm.content}
                onChange={e =>
                  setModelForm(f => ({ ...f, content: e.target.value }))
                }
                placeholder="6 kg" // i18n-ignore
              />
            </div>
            <div>
              <Label>{t('mfr.agentType')}</Label>
              <Input
                value={modelForm.agentType}
                onChange={e =>
                  setModelForm(f => ({ ...f, agentType: e.target.value }))
                }
                placeholder="ABC" // i18n-ignore
              />
            </div>
            <div className="sm:col-span-2">
              {/* Price of the device itself — what a replacement costs when it
                  is billed as new (client #60). */}
              <Label>{t('mfr.modelPrice')}</Label>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.05"
                value={modelForm.price}
                onChange={e =>
                  setModelForm(f => ({ ...f, price: e.target.value }))
                }
              />
            </div>
            <div className="sm:col-span-2">
              <Label>{t('table.deviceType')}</Label>
              <Select
                value={modelForm.deviceType}
                onValueChange={v =>
                  setModelForm(f => ({ ...f, deviceType: v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('table.deviceType')} />
                </SelectTrigger>
                <SelectContent>
                  {types.map(dt => (
                    <SelectItem key={dt.id} value={dt.id}>
                      {dt.nameDe}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              {/* The client clarified the manual belongs to the device, not the
                  brand (#51) — same upload flow as the manufacturer dialog, so
                  a model can carry its own PDF and shadow the brand's. */}
              <Label>{t('mfr.modelDoc')}</Label>
              <input
                ref={modelDocRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={async e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const stored = await signAndPut(file, UploadKind.PDF);
                    setModelForm(f => ({ ...f, documentationPdf: stored }));
                  } catch (err) {
                    toast(
                      err instanceof UploadFailedError
                        ? t(err.i18nKey)
                        : t('common.error')
                    );
                  }
                  e.target.value = '';
                }}
              />
              <div className="mt-1 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => modelDocRef.current?.click()}
                >
                  <Upload className="h-3 w-3" />
                  {t('ext.uploadDoc')}
                </Button>
                {modelForm.documentationPdf && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openUpload(modelForm.documentationPdf)}
                  >
                    <ExternalLink className="h-3 w-3" />
                    {t('ext.viewDoc')}
                  </Button>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModelOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={saveModel}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
